import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EndpointDialog, type EndpointPrefill } from "@/components/models/EndpointDialog";
import { VendorMark } from "@/components/models/ModelParts";
import { endpointKind } from "@/components/models/vendor-logos";
import { Button } from "@/components/ui/button";
import { api, apiErrorCode, type BooModelProvider, type PurposeDef } from "@/lib/api";
import { describeModelError } from "@/lib/model-errors";
import { sourceLabel } from "@/lib/models";
import { useInvalidateModels } from "@/pages/org-models/use-org-models";
import { useMutation } from "@tanstack/react-query";
import { ArrowUpRight, KeyRound, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// Endpoints added to this one Boo before the organization could share its own
// models. They keep working; there is no add or edit here, only moving one to
// the organization or removing it.

export function LegacyEndpoints({
  containerId,
  providers,
  canConfigure,
  canManageOrgModels,
  supportsOrgEndpoints,
  orgEndpointNames,
  reservedNames,
  purposeDefs,
}: {
  containerId: string;
  providers: BooModelProvider[];
  canConfigure: boolean;
  canManageOrgModels: boolean;
  supportsOrgEndpoints: boolean;
  orgEndpointNames: string[];
  reservedNames: string[];
  purposeDefs: PurposeDef[];
}) {
  const invalidate = useInvalidateModels();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<BooModelProvider | null>(null);
  const [prefill, setPrefill] = useState<EndpointPrefill | null>(null);

  const move = useMutation({
    mutationFn: (name: string) => api.models.moveBooProviderToOrg(containerId, name),
    onMutate: (name) =>
      setErrors((e) => {
        const next = { ...e };
        delete next[name];
        return next;
      }),
    onSuccess: (_res, name) => {
      toast.success(`Moved. Every Boo in your organization can use ${sourceLabel({ name })} now.`);
      void invalidate();
    },
    onError: (err, name) => {
      const p = providers.find((x) => x.name === name);
      // The key can't be carried over: ask for it and add the endpoint anew.
      if (apiErrorCode(err) === "key_required" && p) {
        setPrefill({
          name: p.name,
          baseUrl: p.baseUrl,
          models: p.models,
          reason: `This Boo's access key for ${sourceLabel(p)} can't be moved. Enter it again to add these models to your organization.`,
        });
        return;
      }
      setErrors((e) => ({ ...e, [name]: describeModelError(err, { defs: purposeDefs, source: sourceLabel({ name }) }) }));
    },
  });

  const remove = useMutation({
    mutationFn: (name: string) => api.models.deleteBooProvider(containerId, name),
    onSuccess: () => {
      toast.success("Removed. Jobs that used it now follow the organization default.");
      setRemoving(null);
      void invalidate();
    },
  });

  return (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">Only on this Boo</h3>
        <p className="text-sm text-muted-foreground">
          These models were added to this Boo alone, before your
          organization could share its own models. They keep working. Move them to your organization so every Boo can
          use them.
        </p>
      </div>
      <ul className="divide-y rounded-lg border">
        {providers.map((p) => (
          <li key={p.name} className="flex flex-wrap items-start gap-3 px-4 py-3">
            <VendorMark vendor="" custom endpoint={endpointKind(p.baseUrl, p.name)} size="lg" className="mt-0.5" />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{sourceLabel(p)}</span>
                {p.keyNeeded && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200 ring-inset dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30">
                    <KeyRound className="size-3" />
                    Access key needed
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{p.models.join(", ")}</p>
              {errors[p.name] && <p className="text-xs text-destructive">{errors[p.name]}</p>}
              {canConfigure && canManageOrgModels && !supportsOrgEndpoints && (
                <p className="text-xs text-muted-foreground">Update this Boo to move it to your organization.</p>
              )}
            </div>
            {canConfigure && (
              <div className="flex shrink-0 items-center gap-1">
                {canManageOrgModels && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!supportsOrgEndpoints}
                    loading={move.isPending && move.variables === p.name}
                    onClick={() => move.mutate(p.name)}
                  >
                    <ArrowUpRight className="size-3.5" />
                    Move to organization
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    remove.reset();
                    setRemoving(p);
                  }}
                >
                  <Trash2 className="size-3.5" />
                  Remove
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Remove ${removing ? sourceLabel(removing) : "these models"}?`}
        description="Jobs using its models go back to the organization default, and its saved access key is deleted."
        confirmLabel="Remove"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.name)}
      >
        {remove.isError && (
          <p role="alert" className="text-sm text-destructive">
            {describeModelError(remove.error, { defs: purposeDefs })}
          </p>
        )}
      </ConfirmDialog>

      <EndpointDialog
        open={prefill !== null}
        onOpenChange={(o) => !o && setPrefill(null)}
        prefill={prefill}
        takenNames={orgEndpointNames}
        reservedNames={reservedNames}
        purposeDefs={purposeDefs}
        onSaved={() => {
          setPrefill(null);
          void invalidate();
        }}
      />
    </section>
  );
}
